/**
 * The collapsed JSON payload block used by tool cards (18.08.2026).
 *
 * Extracted verbatim from AIChatPanel.js so the rich result views in
 * toolResultViews.js can reuse it without importing back into the panel
 * (which would be a cycle: AIChatPanel → toolResultViews → AIChatPanel).
 *
 * Nothing about the rendering changed in the move.
 */
import React, { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';
import MonacoReadOnly from '../ui/MonacoReadOnly';

/** Best-effort pretty-print: JSON when it parses, plain text when it doesn't. */
export function formatToolPayload(value) {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return { text: JSON.stringify(parsed, null, 2), language: 'json' };
    } catch {
      return { text: value, language: 'text' };
    }
  }
  return { text: JSON.stringify(value, null, 2), language: 'json' };
}

export default function ToolCodeBlock({ label, value }) {
  const [copied, setCopied] = useState(false);
  const { text, language } = formatToolPayload(value);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }, [text]);

  return (
    <div className="mt-2 w-full min-w-0">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="ai-chat-code-label mb-0">{label}</div>
        <button
          type="button"
          onClick={handleCopy}
          className="ai-chat-copy-btn"
          title="Copy to clipboard"
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check size={12} className="text-green-500 dark:text-green-400" /> : <Copy size={12} />}
        </button>
      </div>
      <MonacoReadOnly
        value={text}
        language={language}
        maxHeight={180}
        className="ai-chat-code-block"
      />
    </div>
  );
}
